/**
 * PartnerServiceAreaPage — /partner/service-area (Phase J).
 *
 * Partners declare their geographic coverage as a `primary_zip` plus
 * up to N `additional_zips`. Cap is tier-dependent
 * (Standard 5 / Preferred 10 / Elite 25; sourced from platform_config
 * via the row's `tier_cap` field — server owns tier resolution, FE
 * only renders it).
 *
 * Interaction model:
 *   - Primary ZIP: single text input with strict 5-digit validation
 *     on-blur.
 *   - Additional ZIPs: chip picker (type a 5-digit ZIP, press Enter,
 *     chip appears; backspace deletes last chip; x on each chip
 *     removes it individually; fully keyboard-navigable).
 *   - Cap counter: `N of M` label above the chip picker.
 *   - Over-cap yellow banner + Save disabled when N > M (downgrade
 *     grace).
 *
 * Map preview (Mapbox GL JS): NOT wired in this dispatch —
 * `REACT_APP_MAPBOX_TOKEN` is not present in `.env`. A "Coverage
 * preview" panel renders a text-based grid of selected ZIPs with
 * city labels instead. This is the PJ5 "map failure → form fully
 * usable" default state. When the Mapbox token lands, the fallback
 * panel gets replaced with a Mapbox instance — the chip picker is
 * source-of-truth either way.
 *
 * Copy discipline (Family H): factual only. No "coverage guaranteed",
 * no "we service X neighborhoods". The neighborhood label is derived
 * from the ZIP → city map returned by the server.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  MapPin,
  Loader2,
  Save,
  Plus,
  X,
  AlertTriangle,
} from "lucide-react";

import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import FloatingThemeToggle from "@/components/FloatingThemeToggle";

const ZIP_RE = /^\d{5}$/;

function fmtCap(current, cap) {
  return `${current} of ${cap}`;
}

/* ------------------------------------------------------------------ */
export default function PartnerServiceAreaPage() {
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [primary, setPrimary] = useState("");
  const [additional, setAdditional] = useState([]);
  const [pendingInput, setPendingInput] = useState("");
  const [saving, setSaving] = useState(false);
  const chipInputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/partner/service-area/me");
      setRow(data);
      setPrimary(data.primary_zip || "");
      setAdditional(data.additional_zips || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const cap = row?.tier_cap ?? 5;
  const tierName = row?.tier_name || "Standard";
  const overCap = additional.length > cap;

  const primaryValid = ZIP_RE.test(primary);

  const addChipFromInput = () => {
    const v = pendingInput.trim();
    if (!ZIP_RE.test(v)) {
      toast.error("ZIP must be 5 digits.");
      return;
    }
    if (v === primary) {
      toast.error("Primary ZIP is already implied — no need to re-add it.");
      return;
    }
    if (additional.includes(v)) {
      toast.error("Already added.");
      return;
    }
    if (additional.length + 1 > cap) {
      toast.error(
        `Your ${tierName} tier supports ${cap} additional ZIPs.`,
      );
      return;
    }
    setAdditional((prev) => [...prev, v]);
    setPendingInput("");
  };

  const removeChip = (zip) => {
    setAdditional((prev) => prev.filter((z) => z !== zip));
  };

  const handleChipKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addChipFromInput();
    } else if (
      e.key === "Backspace" &&
      pendingInput === "" &&
      additional.length > 0
    ) {
      // Backspace on empty input deletes the last chip.
      setAdditional((prev) => prev.slice(0, -1));
    }
  };

  const save = async () => {
    if (!primaryValid) {
      toast.error("Enter a valid 5-digit primary ZIP.");
      return;
    }
    if (overCap) {
      toast.error(
        `Reduce additional ZIPs to ${cap} or fewer before saving.`,
      );
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/partner/service-area/save", {
        primary_zip: primary,
        additional_zips: additional,
      });
      setRow(data);
      toast.success("Service area saved.");
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg =
        (d && typeof d === "object" && d.message) ||
        formatApiErrorDetail(d) ||
        "Save failed.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const container = useMemo(
    () => ({
      maxWidth: 1100,
      margin: "0 auto",
      padding: "40px 20px 80px",
      fontFamily:
        "-apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif",
    }),
    [],
  );

  if (loading) {
    return (
      <div style={container} data-testid="partner-service-area-loading">
        <Loader2 className="animate-spin" size={22} />
      </div>
    );
  }
  if (!row) return null;

  return (
    <div style={container} data-testid="partner-service-area-page">
      <FloatingThemeToggle />
      <header style={{ marginBottom: 24 }}>
        <span
          style={{
            color: "#F89623",
            fontWeight: 700,
            fontSize: 13,
            letterSpacing: 1.5,
            textTransform: "uppercase",
          }}
          data-testid="partner-service-area-eyebrow"
        >
          Service area
        </span>
        <h1
          style={{
            fontSize: 32,
            fontWeight: 800,
            margin: "8px 0 12px 0",
            color: "#374751",
          }}
          data-testid="partner-service-area-heading"
        >
          Where do you pluck?
        </h1>
        <p style={{ color: "#4b5563", lineHeight: 1.6 }}>
          Pick your home ZIP as the primary, then add up to{" "}
          <strong>{cap}</strong> additional ZIPs where you're happy to
          drive. Customers in these ZIPs will see you as an option when
          they book.
        </p>
      </header>

      {overCap && (
        <div
          style={{
            padding: "12px 14px",
            marginBottom: 16,
            background: "#FFF8EE",
            border: "1px solid #F89623",
            borderRadius: 12,
            color: "#8B5F0E",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
          data-testid="partner-service-area-over-cap-banner"
        >
          <AlertTriangle size={18} />
          <span>
            Your <strong>{tierName}</strong> tier supports{" "}
            <strong>{cap}</strong> additional ZIPs. You have{" "}
            <strong>{additional.length}</strong> configured. Remove{" "}
            {additional.length - cap} to save changes.
          </span>
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 24,
        }}
      >
        {/* Left — form */}
        <Card
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: 16,
            padding: 4,
          }}
          data-testid="partner-service-area-form"
        >
          <CardHeader>
            <CardTitle
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                color: "#374751",
                fontWeight: 700,
              }}
            >
              <MapPin size={20} color="#F89623" /> ZIP configuration
            </CardTitle>
          </CardHeader>
          <CardContent style={{ display: "grid", gap: 16 }}>
            <div>
              <Label htmlFor="primary-zip">Primary ZIP</Label>
              <Input
                id="primary-zip"
                inputMode="numeric"
                maxLength={5}
                value={primary}
                onChange={(e) =>
                  setPrimary(e.target.value.replace(/\D/g, "").slice(0, 5))
                }
                placeholder="94103"
                data-testid="partner-service-area-primary-input"
              />
              {primary && !primaryValid && (
                <span
                  style={{ color: "#991b1b", fontSize: 13 }}
                  data-testid="partner-service-area-primary-error"
                >
                  Must be a 5-digit ZIP.
                </span>
              )}
            </div>

            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                }}
              >
                <Label htmlFor="additional-zip">Additional ZIPs</Label>
                <span
                  style={{
                    fontSize: 13,
                    color: overCap ? "#991b1b" : "#6b7280",
                    fontWeight: 600,
                  }}
                  data-testid="partner-service-area-cap-counter"
                >
                  {fmtCap(additional.length, cap)}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 6,
                  padding: 8,
                  border: "1px solid #d1d5db",
                  borderRadius: 12,
                  minHeight: 60,
                  background: "#fff",
                }}
                data-testid="partner-service-area-chip-container"
                onClick={() => chipInputRef.current?.focus()}
              >
                {additional.map((z) => (
                  <span
                    key={z}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      padding: "4px 10px",
                      borderRadius: 999,
                      background: "#EEF2FF",
                      color: "#3730A3",
                      fontWeight: 600,
                      fontSize: 13,
                    }}
                    data-testid={`partner-service-area-chip-${z}`}
                  >
                    {z}
                    <button
                      onClick={() => removeChip(z)}
                      aria-label={`Remove ${z}`}
                      style={{
                        border: 0,
                        background: "transparent",
                        cursor: "pointer",
                        padding: 0,
                        display: "flex",
                      }}
                      data-testid={`partner-service-area-chip-remove-${z}`}
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
                <input
                  ref={chipInputRef}
                  id="additional-zip"
                  inputMode="numeric"
                  maxLength={5}
                  value={pendingInput}
                  onChange={(e) =>
                    setPendingInput(
                      e.target.value.replace(/\D/g, "").slice(0, 5),
                    )
                  }
                  onKeyDown={handleChipKeyDown}
                  placeholder="Type ZIP + Enter"
                  style={{
                    flex: "1 0 120px",
                    border: 0,
                    outline: "none",
                    padding: "4px 6px",
                    fontSize: 14,
                    background: "transparent",
                  }}
                  data-testid="partner-service-area-chip-input"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={addChipFromInput}
                  data-testid="partner-service-area-chip-add-btn"
                >
                  <Plus size={14} />
                </Button>
              </div>
              <p style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>
                Tip: press <kbd>Enter</kbd> to add, <kbd>Backspace</kbd> on an
                empty line to remove the last chip.
              </p>
            </div>

            <Button
              onClick={save}
              disabled={saving || !primaryValid || overCap}
              data-testid="partner-service-area-save-btn"
              style={{ alignSelf: "flex-start" }}
            >
              {saving ? (
                <Loader2 className="animate-spin" size={16} />
              ) : (
                <>
                  <Save size={16} style={{ marginRight: 6 }} /> Save service area
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Right — coverage preview (list-mode; Mapbox will replace
             this panel once REACT_APP_MAPBOX_TOKEN lands). */}
        <Card
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: 16,
            padding: 4,
          }}
          data-testid="partner-service-area-preview"
        >
          <CardHeader>
            <CardTitle
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                color: "#374751",
                fontWeight: 700,
              }}
            >
              <MapPin size={20} color="#F89623" /> Coverage preview
            </CardTitle>
          </CardHeader>
          <CardContent style={{ color: "#4b5563" }}>
            <p style={{ marginBottom: 12 }}>
              Partners who selected each ZIP will match customer bookings
              at that ZIP.
            </p>
            {primary ? (
              <div
                style={{ marginBottom: 12 }}
                data-testid="partner-service-area-preview-primary"
              >
                <strong style={{ color: "#F89623" }}>Primary:</strong>{" "}
                <code>{primary}</code>
              </div>
            ) : (
              <div style={{ color: "#9ca3af", marginBottom: 12 }}>
                Add a primary ZIP to seat your coverage.
              </div>
            )}
            {additional.length > 0 && (
              <div data-testid="partner-service-area-preview-additional">
                <strong style={{ color: "#3730A3" }}>Additional:</strong>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 6,
                    marginTop: 6,
                  }}
                >
                  {additional.map((z) => (
                    <span
                      key={z}
                      style={{
                        padding: "2px 8px",
                        borderRadius: 999,
                        background: "#f3f4f6",
                        fontSize: 12,
                      }}
                    >
                      {z}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
